/**
 * The registration rules, client side.
 *
 * A deliberate mirror of `RegisterBody` in `api/routes/identities.ts`, written for
 * the same reason `station.ts` mirrors the provisioning route: the server is the
 * gate and nothing here is trusted, but an operator reading a tag off a physical
 * card deserves to be told about a 40-character paste before they submit it. Both
 * halves cite the same bounds so a drift between them is visible rather than
 * silent.
 *
 * Every function returns `undefined` for an empty field. Whether a blank is
 * allowed is the form's required-handling to decide; complaining that someone has
 * not typed yet is noise.
 */

/**
 * OCPP 1.6's IdTokenSchema bound, which the schema restates as
 * `CHECK (char_length(id_tag) BETWEEN 1 AND 36)`. Inclusive.
 */
export const ID_TAG_MAX = 36;

/** The server's `z.string().max(200)` on `label`. */
export const IDENTITY_LABEL_MAX = 200;
/** The column's CHECK (docs/session-push-plan.md P4). */
export const IDENTITY_EXTERNAL_REF_MAX = 100;

/**
 * ISO 3779: seventeen characters, digits and capitals, never I, O or Q. The
 * server's `VIN` pattern on `RegisterBody`, case-insensitive here because the
 * value is uppercased before it is sent.
 */
const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/i;

export function vinError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (!VIN_PATTERN.test(raw.trim())) {
    return "A VIN is 17 letters and digits, and never contains I, O or Q.";
  }

  return undefined;
}

/**
 * What is actually registered. A VIN is uppercased because that is how a vehicle
 * presents it; an RFID tag keeps its case, since a station compares it byte for
 * byte and a "tidied" tag would authorise nothing.
 */
export function normaliseIdTag(raw: string, tokenType: "rfid" | "vin"): string {
  const trimmed = raw.trim();
  return tokenType === "vin" ? trimmed.toUpperCase() : trimmed;
}

export function idTagError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (raw.trim() === "") return "An ID tag cannot be only spaces.";

  if (raw.length > ID_TAG_MAX) {
    return `At most ${ID_TAG_MAX} characters — that is the limit OCPP puts on an ID tag.`;
  }

  /**
   * The tag is compared byte-for-byte against what the station puts in
   * `Authorize.req`, so interior whitespace is not a style violation — it is a card
   * that can never authorise. In practice it means a copy-paste artefact.
   */
  if (/\s/.test(raw)) {
    return "No spaces inside an ID tag. This must match exactly what the card presents.";
  }

  return undefined;
}

export function identityLabelError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (raw.trim() === "") return "A card needs a name.";

  if (raw.length > IDENTITY_LABEL_MAX) {
    return `At most ${IDENTITY_LABEL_MAX} characters.`;
  }

  return undefined;
}

/**
 * Only a value that is not a date at all. A past date is legitimate — a contractor
 * card imported mid-contract — so it is `expiryDateNotice`'s business, not this
 * function's, and it must not block submission.
 */
export function expiryDateError(raw: string): string | undefined {
  if (raw === "") return undefined;

  if (Number.isNaN(parsed(raw))) {
    return "Not a date the browser can read. Use the picker, or type it as YYYY-MM-DD.";
  }

  return undefined;
}

/**
 * Advisory. Spec 6.4's decision table answers `Expired` for a tag whose
 * `expiry_date` has passed, so a card registered with one authorises nowhere. The
 * server accepts it on purpose; this is how an operator who mistyped the year finds
 * out before a driver does.
 */
export function expiryDateNotice(raw: string): string | undefined {
  const at = parsed(raw);
  // A malformed value is `expiryDateError`'s to report. Two messages under one
  // field about the same mistake is worse than one.
  if (raw === "" || Number.isNaN(at)) return undefined;

  if (at <= Date.now()) {
    return "This date is in the past, so the card will answer Expired at every charger.";
  }

  return undefined;
}

function parsed(raw: string): number {
  return new Date(raw).getTime();
}

/**
 * Whether an existing card may be called `tokenType`. The tag cannot be edited, so
 * a VIN has to be the stored tag exactly — upper case, since nothing uppercases it
 * on an edit — which is the server's case-sensitive `VIN_SHAPE` on `EditBody`.
 */
export function tokenTypeError(storedIdTag: string, tokenType: "rfid" | "vin"): string | undefined {
  if (tokenType === "rfid" || STORED_VIN.test(storedIdTag)) return undefined;
  return "This tag is not a VIN — a VIN is 17 capital letters and digits, without I, O or Q.";
}

const STORED_VIN = /^[A-HJ-NPR-Z0-9]{17}$/;
